import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { clientAuthorizedPost } from 'utils/fetchUtils';
import { API_ROUTES } from 'utils/constants';
import {
  StorageProvider,
  StorageProviderType,
  CreateStorageProviderRequest,
} from 'types/storageProvider';
import Button from 'components/Button';
import { PROVIDER_TYPES, SAMPLE_CREDENTIALS } from './storageProviderSamples';
import styles from './Storage.module.scss';

interface AddStorageProviderModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: (config: StorageProvider) => void;
  onError: (message: string) => void;
}

function AddStorageProviderModal({
  show,
  onHide,
  onSuccess,
  onError,
}: AddStorageProviderModalProps) {
  const [providerType, setProviderType] = useState<StorageProviderType>('s3');
  const [credentialsJsonStr, setCredentialsJsonStr] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = useCallback(() => {
    setProviderType('s3');
    setCredentialsJsonStr(SAMPLE_CREDENTIALS.s3);
  }, []);

  useEffect(() => {
    if (show) {
      setCredentialsJsonStr(SAMPLE_CREDENTIALS[providerType]);
    }
  }, [show, providerType]);

  const handleProviderTypeChange = useCallback(
    (newType: StorageProviderType) => {
      setProviderType(newType);
      setCredentialsJsonStr(SAMPLE_CREDENTIALS[newType]);
    },
    [],
  );

  const handleHide = useCallback(() => {
    resetForm();
    onHide();
  }, [onHide, resetForm]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      let credentialsJson: Record<string, unknown>;
      try {
        credentialsJson = credentialsJsonStr.trim()
          ? (JSON.parse(credentialsJsonStr) as Record<string, unknown>)
          : {};
      } catch {
        onError('Invalid JSON in credentials.');
        return;
      }
      setIsSubmitting(true);
      try {
        const body: CreateStorageProviderRequest = {
          providerType,
          credentialsJson,
        };
        const created = await clientAuthorizedPost<StorageProvider>(
          API_ROUTES.STORAGE_PROVIDERS,
          body as unknown as Record<string, unknown>,
          undefined,
        );
        onSuccess(created);
        handleHide();
      } catch (err) {
        const msg =
          err instanceof Error
            ? err.message
            : 'Failed to create storage provider.';
        onError(msg);
      } finally {
        setIsSubmitting(false);
      }
    },
    [providerType, credentialsJsonStr, onSuccess, onError, handleHide],
  );

  return (
    <Modal show={show} onHide={handleHide} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Add storage provider</Modal.Title>
      </Modal.Header>
      <Form onSubmit={(e) => void handleSubmit(e)}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Provider type</Form.Label>
            <Form.Select
              value={providerType}
              onChange={(e) =>
                handleProviderTypeChange(e.target.value as StorageProviderType)
              }
              aria-label="Provider type"
            >
              {PROVIDER_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Credentials (JSON)</Form.Label>
            <Form.Control
              as="textarea"
              rows={10}
              value={credentialsJsonStr}
              onChange={(e) => setCredentialsJsonStr(e.target.value)}
              className={styles.credentialsInput}
              aria-label="Credentials JSON"
            />
            <Form.Text className="text-muted">
              Replace the placeholder values with your real credentials. Not
              stored in plain text on the server.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleHide} type="button">
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Creating…' : 'Create'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default AddStorageProviderModal;
